import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, otherParty } from '../api/client';
import { dial, normaliseAu } from '../twilio/dial';
import { useDeviceStore } from '../store/device';

const POLL_MS = 10_000;

/**
 * Recent calls panel on the Dashboard (FR-50). Last 10 calls handled by
 * the calling user. Each row carries a Call button (FR-56) that dials the
 * other party via the Voice SDK.
 */
export function RecentCallsPanel() {
  const query = useQuery({
    queryKey: ['recent-calls'],
    queryFn: () => api.recentCalls().then(r => r.calls),
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: false,
  });

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900">Recent calls</h3>
        <span className="text-xs text-slate-400">your last 10 · refreshes every 10s</span>
      </div>
      {query.isLoading && <p className="px-4 py-3 text-xs text-slate-500">Loading…</p>}
      {query.error && <p className="px-4 py-3 text-xs text-red-700">Failed to load recent calls.</p>}
      {query.data && query.data.length === 0 && (
        <p className="px-4 py-6 text-xs text-slate-400 text-center">No calls yet.</p>
      )}
      {query.data && query.data.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm table-fixed">
            <colgroup>
              <col className="w-[18%]" />
              <col className="w-[24%]" />
              <col className="w-[12%]" />
              <col className="w-[14%]" />
              <col className="w-[16%]" />
              <col className="w-[16%]" />
            </colgroup>
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="text-left px-3 py-1.5">When</th>
                <th className="text-left px-3 py-1.5">Number</th>
                <th className="text-left px-3 py-1.5">Direction</th>
                <th className="text-left px-3 py-1.5">Duration</th>
                <th className="text-left px-3 py-1.5">Status</th>
                <th className="text-right px-3 py-1.5"> </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {query.data.map(c => (
                <Row key={`${c.agentUserId}-${c.startedAt}`} call={c} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Row({ call }: { call: { from: string; to: string; direction?: 'inbound' | 'outbound'; startedAt: string; durationSeconds?: number; status: 'in-progress' | 'completed' | 'no-answer' | 'failed' } }) {
  const number = otherParty(call);
  const dir = call.direction ?? 'inbound';
  return (
    <tr>
      <td className="px-3 py-2 text-xs text-slate-600 whitespace-nowrap">{formatRelative(call.startedAt)}</td>
      <td className="px-3 py-2 font-mono text-slate-800 whitespace-nowrap">{formatNumber(number)}</td>
      <td className="px-3 py-2 text-xs text-slate-600">{dir === 'outbound' ? 'outgoing' : 'incoming'}</td>
      <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{formatDuration(call.durationSeconds)}</td>
      <td className="px-3 py-2 whitespace-nowrap"><StatusBadge status={call.status} /></td>
      <td className="px-3 py-2 text-right"><CallButton number={number} /></td>
    </tr>
  );
}

/**
 * Inline "Call" button shared by RecentCallsPanel + CallHistoryPage. Dials
 * the row's number via the existing Voice SDK Device. Disabled when no
 * device is ready, audio isn't unlocked, an active call is already up, or
 * the number isn't a valid AU phone (e.g. for some legacy / `_missed` rows
 * with weird `to` values).
 */
export function CallButton({ number }: { number: string }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const ready = useDeviceStore(s => s.ready);
  const audioUnlocked = useDeviceStore(s => s.audioUnlocked);
  const activeCall = useDeviceStore(s => s.activeCall);

  const normalised = normaliseAu(number);
  const disabled = !ready || !audioUnlocked || !!activeCall || busy || !normalised;

  async function onClick() {
    if (!normalised) return;
    setError(undefined);
    setBusy(true);
    try {
      await dial(normalised);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to call.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <span className="inline-flex flex-col items-end">
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        title={normalised ?? 'Not a valid AU number'}
        className="text-xs px-2 py-1 rounded bg-emerald-100 text-emerald-800 hover:bg-emerald-200 disabled:opacity-30 disabled:cursor-not-allowed"
      >
        {busy ? '…' : 'Call'}
      </button>
      {error && <span className="text-[10px] text-red-700 mt-0.5">{error}</span>}
    </span>
  );
}

function StatusBadge({ status }: { status: 'in-progress' | 'completed' | 'no-answer' | 'failed' }) {
  const cls =
    status === 'completed'
      ? 'bg-emerald-100 text-emerald-800'
      : status === 'in-progress'
        ? 'bg-amber-100 text-amber-800'
        : status === 'no-answer'
          ? 'bg-red-100 text-red-800'
          : 'bg-slate-200 text-slate-700';
  return <span className={`text-[10px] px-2 py-0.5 rounded ${cls}`}>{status}</span>;
}

function formatNumber(raw: string): string {
  if (!raw) return '—';
  if (raw.startsWith('+61') && raw.length === 12) {
    return `${raw.slice(0, 3)} ${raw.slice(3, 6)} ${raw.slice(6, 9)} ${raw.slice(9)}`;
  }
  if (raw.startsWith('+1') && raw.length === 12) {
    return `${raw.slice(0, 2)} (${raw.slice(2, 5)}) ${raw.slice(5, 8)}-${raw.slice(8)}`;
  }
  return raw;
}

function formatDuration(seconds?: number): string {
  if (seconds === undefined || seconds === null) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function formatRelative(iso: string): string {
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return new Date(iso).toLocaleString();
}
